/**
 * Google ML Kit & Intelligent On-Device Message Classifier
 * 
 * Invokes native Google ML Kit NLP APIs on Android via Capacitor Native Plugin
 * with a high-performance local JS fallback for web environments.
 */

import { registerPlugin } from '@capacitor/core';

// Access native MessageClassifier plugin if on Android native bridge
const MessageClassifierNative = registerPlugin('MessageClassifier');

// Language Identification Helper
const LANG_PATTERNS = [
  { code: 'tl', name: 'Tagalog', regex: /\b(salamat|mabuhay|tulong|pag-asa|kamusta|masaya|malungkot|ang|mga|paki|ako|mo|siya|kami|tayo)\b/i },
  { code: 'es', name: 'Spanish', regex: /\b(gracias|hola|amor|triste|esperanza|ayuda|por|favor|bueno|que)\b/i },
  { code: 'en', name: 'English', regex: /\b(thank|thanks|love|light|hope|sad|tired|help|alone|peace|dream|happy|good|great|smile|heal|pain)\b/i },
];

// Keyword Classifier Dictionaries
const CATEGORY_RULES = [
  {
    category: 'Gratitude',
    sentiment: 'positive',
    icon: '✨',
    keywords: ['thank', 'thanks', 'grateful', 'blessed', 'appreciation', 'salamat', 'gracias', 'blessing']
  },
  {
    category: 'Hope & Encouragement',
    sentiment: 'positive',
    icon: '🌟',
    keywords: ['hope', 'light', 'dream', 'heal', 'healed', 'glow', 'shine', 'sun', 'bright', 'future', 'pag-asa', 'esperanza']
  },
  {
    category: 'Love & Warmth',
    sentiment: 'positive',
    icon: '💖',
    keywords: ['love', 'care', 'hug', 'warmth', 'peace', 'smile', 'happy', 'joy', 'smile', 'friend', 'mahal', 'amor']
  },
  {
    category: 'Distress & Heavy Heart',
    sentiment: 'distressed',
    icon: '🌧️',
    keywords: ['sad', 'lonely', 'pain', 'tired', 'dark', 'hurt', 'lost', 'cry', 'rain', 'heavy', 'broken', 'tulong', 'triste', 'alone']
  },
  {
    category: 'Anger & Frustration',
    sentiment: 'distressed',
    icon: '🔥',
    keywords: ['angry', 'hate', 'burn', 'scream', 'mad', 'fire', 'danger', 'upset', 'rage', 'storm']
  }
];

/**
 * Classifies a text message.
 * @param {string} text 
 * @returns {Promise<{ category: string, sentiment: string, language: string, confidence: number, icon: string }>}
 */
export async function classifyMessageWithMLKit(text) {
  if (!text || text.trim().length === 0) {
    return {
      category: 'General',
      sentiment: 'neutral',
      language: 'English',
      confidence: 1.0,
      icon: '💭'
    };
  }

  // 1. Try Native Google ML Kit plugin first if available on Android
  try {
    if (window.Capacitor && window.Capacitor.isNativePlatform()) {
      const nativeResult = await MessageClassifierNative.classifyMessage({ text });
      if (nativeResult && nativeResult.category) {
        console.log('[ML Kit Native] Classification result:', nativeResult);
        return nativeResult;
      }
    }
  } catch (err) {
    console.warn('[ML Kit Native] Plugin not available, using high-precision fallback:', err);
  }

  // 2. High-precision JS Classifier Fallback
  const lower = text.toLowerCase();

  // Language Detection
  let detectedLang = 'English';
  for (const lang of LANG_PATTERNS) {
    if (lang.regex.test(lower)) {
      detectedLang = lang.name;
      break;
    }
  }

  // Category & Sentiment Matching
  let matchedCategory = 'General';
  let matchedSentiment = 'positive'; // Default safe tone
  let maxScore = 0;
  let matchedIcon = '💭';

  for (const rule of CATEGORY_RULES) {
    let score = 0;
    for (const kw of rule.keywords) {
      if (lower.includes(kw)) {
        score += 1;
      }
    }
    if (score > maxScore) {
      maxScore = score;
      matchedCategory = rule.category;
      matchedSentiment = rule.sentiment;
      matchedIcon = rule.icon;
    }
  }

  // Fallback sentiment heuristics if no explicit keyword hit
  if (maxScore === 0) {
    const distressedHits = (lower.match(/(bad|hard|struggling|scared|fear|worried|anxious)/g) || []).length;
    if (distressedHits > 0) {
      matchedSentiment = 'distressed';
      matchedCategory = 'Distress & Heavy Heart';
      matchedIcon = '🌙';
    }
  }

  const confidence = maxScore > 0 ? Math.min(0.60 + (maxScore * 0.15), 0.98) : 0.70;

  return {
    category: matchedCategory,
    sentiment: matchedSentiment,
    language: detectedLang,
    confidence: Number(confidence.toFixed(2)),
    icon: matchedIcon
  };
}
